import { CardNumberElement } from "@stripe/react-stripe-js";
import type { StripeCardNumberElementChangeEvent } from "@stripe/stripe-js";
import { CardBrandIcon } from "@freightsimple/ui-core/Display/CardBrandIcon";
import { FieldShell } from "@freightsimple/ui-core/Controls/FieldShell";
import type { ErrorMessageType } from "@freightsimple/ui-core/FormLayout/FormItem";
import { useControlValueTextStyle } from "@freightsimple/ui-core/Controls/useControlValueTextStyle";
import { useThemeContext } from "@freightsimple/ui-core/Theme/useThemeContext";

import { useState } from "react";
import { creditCardsStripeFieldOptions } from "@/Shared/CreditCards/Helpers/creditCardsStripeFieldOptions";

interface CreditCardNumberProps {
  onBrandChange?: ((brandName: string) => void) | undefined;
  onError: (message: ErrorMessageType) => void;
  errorMessage: ErrorMessageType;
  validated: boolean;
}

// The Stripe card-number iframe sits inside a ui-core FieldShell so it reads
// as a boxed input; the detected brand rides in the shell's endAdornment (the
// legacy CreditCardLogo overlay). The red border + error message come from the
// wrapping FormItem (CreditCardsCardPanel). Focus/blur come from Stripe's own events — a
// cross-origin iframe won't trigger the shell's `:focus-within`.
function CreditCardsNumber(props: CreditCardNumberProps) {
  const { resolvedTheme } = useThemeContext();
  const valueText = useControlValueTextStyle();
  const [brand, setBrand] = useState<string>();
  const [focused, setFocused] = useState(false);

  const knownBrand = brand && brand !== "unknown" ? brand : undefined;

  return (
    <FieldShell
      focused={focused}
      data-testid="credit-card-number"
      endAdornment={
        knownBrand ? <CardBrandIcon brand={knownBrand} size="sm" /> : undefined
      }
    >
      <CardNumberElement
        options={creditCardsStripeFieldOptions(resolvedTheme, valueText)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(event: StripeCardNumberElementChangeEvent) => {
          setBrand(event.brand);
          props.onBrandChange?.(event.brand);
          props.onError(event.error?.message);
        }}
      />
    </FieldShell>
  );
}
export default CreditCardsNumber;
